import React, { useMemo, useState } from 'react';
import { User, UserProject, InviteUsersData } from '../types';
import { Trash2, Plus, Mail, Loader2, Upload, Users as UsersIcon, Search, UserX } from 'lucide-react';
import { Dialog, DialogPanel, DialogTitle, Menu, Transition } from '@headlessui/react';
import toast from 'react-hot-toast';
import { InviteUsersModal } from './InviteUsersModal';
import { ChevronDown } from 'lucide-react';

interface ProjectUser extends User {
  userProject: UserProject;
}

interface UserTableProps {
  users: ProjectUser[];
  projectId: string;
  onAddUser: (email: string, role: 'attempt' | 'review') => Promise<void>;
  onUpdateReviewLevel: (userId: string, reviewLevel: number) => Promise<void>;
  onRemoveUser: (userId: string) => Promise<void>;
  loading?: boolean;
  onInviteUsers?: (data: InviteUsersData) => Promise<void>;
}

export const UserTable: React.FC<UserTableProps> = ({
  users,
  onAddUser,
  onUpdateReviewLevel,
  onRemoveUser,
  loading = false,
  onInviteUsers,
}) => {
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<'attempt' | 'review'>('attempt');
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [updatingUsers, setUpdatingUsers] = useState<Set<string>>(new Set());
  const [removingUsers, setRemovingUsers] = useState<Set<string>>(new Set());
  const [showRemoveDialog, setShowRemoveDialog] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteInitialTab, setInviteInitialTab] = useState<'csv' | 'emails' | 'sourcing'>('csv');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'attempt' | 'review'>('all');

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return users.filter(user => {
      const isReview = (user.userProject.review_level ?? -1) !== -1;
      if (roleFilter === 'attempt' && isReview) return false;
      if (roleFilter === 'review' && !isReview) return false;

      if (!query) return true;
      const haystack = `${user.full_name || ''} ${user.email || ''}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [users, searchQuery, roleFilter]);

  const attemptCount = useMemo(() => users.filter(u => (u.userProject.review_level ?? -1) === -1).length, [users]);
  const reviewCount = useMemo(() => users.filter(u => (u.userProject.review_level ?? -1) !== -1).length, [users]);

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isFormValid = newEmail.trim() !== '' && isValidEmail(newEmail) && !isAddingUser;

  const formatDate = useMemo(() => (isoString: string) => {
    const d = new Date(isoString);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }, []);

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setIsAddingUser(true);
    try {
      await onAddUser(newEmail.trim(), newRole);
      setNewEmail('');
      setNewRole('attempt');
      toast.success('User added successfully and invitation sent!');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add user');
    } finally {
      setIsAddingUser(false);
    }
  };

  const handleReviewLevelChange = async (userId: string, newLevel: number) => {
    setUpdatingUsers(prev => new Set(prev).add(userId));
    try {
      await onUpdateReviewLevel(userId, newLevel);
      toast.success('User role updated successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update user role');
    } finally {
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const handleRemoveUser = async (userId: string) => {
    setRemovingUsers(prev => new Set(prev).add(userId));
    setShowRemoveDialog(null);
    try {
      await onRemoveUser(userId);
      toast.success('User removed from project');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to remove user');
    } finally {
      setRemovingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  // Minimal role control for table rows
  const RoleSelect: React.FC<{
    userId: string;
    currentLevel: number;
    disabled?: boolean;
    isUpdating?: boolean;
  }> = ({ userId, currentLevel, disabled, isUpdating }) => {
    const value = currentLevel === -1 ? 'attempt' : 'review';
    return (
      <div className="relative inline-flex items-center">
        <select
          value={value}
          onChange={(e) =>
            handleReviewLevelChange(
              userId,
              e.target.value === 'attempt' ? -1 : 0
            )
          }
          disabled={!!disabled}
          className="appearance-none pr-6 pl-3 py-1.5 rounded-md border border-zinc-300 bg-white text-sm text-zinc-900 focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
          aria-label="Change role"
        >
          <option value="attempt">Attempt</option>
          <option value="review">Review</option>
        </select>
        {isUpdating && (
          <Loader2 className="absolute right-1.5 h-3.5 w-3.5 animate-spin" style={{ color: 'var(--accent)' }} />
        )}
      </div>
    );
  };

  const getInitials = (name?: string, email?: string) => {
    if (name) {
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return email ? email[0].toUpperCase() : '?';
  };

  const userToRemove = users.find(u => u.id === showRemoveDialog);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-5 bg-slate-200 rounded w-48" />
          <div className="h-10 bg-slate-100 rounded" />
          <div className="space-y-2">
            <div className="h-12 bg-slate-50 rounded" />
            <div className="h-12 bg-slate-50 rounded" />
            <div className="h-12 bg-slate-50 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-200">
          <div className="space-y-4">
            {/* Row 1: Title + Invite */}
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-1">
                <h3 className="text-base font-semibold text-slate-900">Members</h3>
                <div className="text-sm text-slate-600 flex gap-3">
                  <span>{users.length} total</span>
                  <span className="text-slate-400">·</span>
                  <span>{attemptCount} attempt</span>
                  <span className="text-slate-400">·</span>
                  <span>{reviewCount} review</span>
                </div>
              </div>
              <Menu as="div" className="relative inline-block text-left z-20">
                <Menu.Button className="inline-flex items-center h-9 px-3.5 rounded-full text-white text-sm font-medium"
                  style={{ backgroundColor: 'var(--accent)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent)')}
                >
                  Invite
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Menu.Button>
                <Transition
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-md border border-slate-200 bg-white shadow-lg focus:outline-none z-50">
                    <div className="py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => { setInviteInitialTab('emails'); setShowInviteModal(true); }}
                            className={`w-full px-3 py-2 text-left text-sm inline-flex items-center ${active ? 'bg-slate-50' : ''}`}
                          >
                            <Mail className="w-4 h-4 mr-2" /> Add by email / list
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => { setInviteInitialTab('csv'); setShowInviteModal(true); }}
                            className={`w-full px-3 py-2 text-left text-sm inline-flex items-center ${active ? 'bg-slate-50' : ''}`}
                          >
                            <Upload className="w-4 h-4 mr-2" /> Upload CSV
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => { setInviteInitialTab('sourcing'); setShowInviteModal(true); }}
                            className={`w-full px-3 py-2 text-left text-sm inline-flex items-center ${active ? 'bg-slate-50' : ''}`}
                          >
                            <UsersIcon className="w-4 h-4 mr-2" /> Source users
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>

            {/* Row 2: Search/Filter (left) + Add group (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search members"
                    className="w-full h-9 pl-9 pr-3 rounded-md border border-zinc-300 focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                    aria-label="Search users"
                  />
                </div>
                <div className="inline-flex rounded-md bg-slate-100 p-1">
                  {(['all','attempt','review'] as const).map((val) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setRoleFilter(val)}
                      className={`h-9 px-3 text-sm rounded-md ${
                        roleFilter === val ? 'bg-white shadow border border-slate-200 text-slate-900' : 'text-slate-600'
                      }`}
                    >
                      {val[0].toUpperCase() + val.slice(1)}
                    </button>
                  ))}
                </div>
                {/* Reset button removed per request */}
              </div>
              {/* Add by email section removed per request; Invite button remains above */}
            </div>
          </div>
        </div>

        {/* Table */}
        {users.length === 0 ? (
          <div className="px-6 py-14 text-center">
            <UserX className="mx-auto h-12 w-12 text-slate-300" />
            <h4 className="mt-3 text-slate-900 font-medium">No members yet</h4>
            <p className="text-sm text-slate-600 mt-1">Invite teammates to collaborate on this project.</p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => { setInviteInitialTab('csv'); setShowInviteModal(true); }}
                className="inline-flex items-center px-3 py-2 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                <Upload className="w-4 h-4 mr-1" /> Bulk upload
              </button>
              <button
                type="button"
                onClick={() => { setInviteInitialTab('emails'); setShowInviteModal(true); }}
                className="inline-flex items-center px-3 py-2 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                <Mail className="w-4 h-4 mr-1" /> Paste emails
              </button>
              <button
                type="button"
                onClick={() => { setInviteInitialTab('sourcing'); setShowInviteModal(true); }}
                className="inline-flex items-center px-3 py-2 rounded-full"
                style={{ color: 'var(--accent)', boxShadow: 'inset 0 0 0 1px color-mix(in oklab, var(--accent) 30%, white)' }}
              >
                <UsersIcon className="w-4 h-4 mr-1" /> Source
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Joined</th>
                  <th className="px-6 py-2" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredUsers.map((user) => {
                  const isUpdating = updatingUsers.has(user.id);
                  const isRemoving = removingUsers.has(user.id);
                  const currentLevel = user.userProject.review_level ?? -1;

                  return (
                    <tr key={user.id} className={isRemoving ? 'opacity-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
                            {user.avatar_url ? (
                              <img className="h-full w-full object-cover" src={user.avatar_url} alt={user.full_name || user.email} />
                            ) : (
                              <span className="text-slate-600 text-sm font-medium">{getInitials(user.full_name, user.email)}</span>
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-slate-900">{user.full_name || 'No name'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{user.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <RoleSelect
                          userId={user.id}
                          currentLevel={currentLevel}
                          disabled={isUpdating || isRemoving}
                          isUpdating={isUpdating}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{formatDate(user.userProject.created_at)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setShowRemoveDialog(user.id)}
                            disabled={isRemoving}
                            className="inline-flex items-center px-2.5 py-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-10 text-center text-sm text-slate-600">No members match your filters</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Remove confirmation */}
      <Dialog open={showRemoveDialog !== null} onClose={() => setShowRemoveDialog(null)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
          <DialogPanel className="max-w-sm w-full space-y-4 border border-slate-200 bg-white p-6 rounded-xl shadow-xl">
            <DialogTitle className="font-semibold text-slate-900 text-base">Remove user</DialogTitle>
            <p className="text-sm text-slate-600">
              Are you sure you want to remove <strong>{userToRemove?.full_name || userToRemove?.email}</strong> from this project? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowRemoveDialog(null)}
                className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={() => showRemoveDialog && handleRemoveUser(showRemoveDialog)}
                className="px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Remove
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Invite / Bulk upload / Source modal */}
      {onInviteUsers && (
        <InviteUsersModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          onInvite={onInviteUsers}
          loading={false}
          initialTab={inviteInitialTab}
        />
      )}
    </>
  );
};